import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, filter, map, switchMap, take, tap } from 'rxjs';
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { TokenStorageService } from 'src/app/authentication/storage/token-storage.service';
import { companyHasFeature } from 'src/app/shared/helpers/tenant';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { selectFeature as selectCurrentEmployeeState } from 'src/app/stores/global/current-employee/current-employee-selector';
import * as EmployeeActions from "src/app/stores/global/employees/employee-actions";

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {
  constructor(
    private router: Router,
    private tokenStorageService : TokenStorageService,
    private permissionStorage : PermissionStorageService,
    private store: Store<AppStateInterface>
    ) {
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
      if(!this.tokenStorageService.getToken()){
        return this.router.createUrlTree(["/auth"]);
      }
      else if(!this.permissionStorage.getPermissions()?.includes(route.data['permission'])){
        return this.router.createUrlTree(["/403"]);
      }
      else if(route.data['requiresCompanyFeature']){
        return this.checkCompanyFeature(route.data['requiresCompanyFeature']);
      }
      else {
        return true
      }

      // return true
  }

  // Routes of optional modules (e.g. inventory) additionally require the feature flag of the user's company.
  // The current employee is loaded asynchronously, so wait for the store before deciding.
  // Note: `isLoading` of the current-employee state must not be used here - other pages dispatch getCurrentEmployee
  // again, but the effect only runs once (take(1)), so `isLoading` can stay true forever.
  private checkCompanyFeature(feature: string): Observable<boolean | UrlTree> {
    const employeeState$ = this.store.pipe(select(selectCurrentEmployeeState));

    return employeeState$.pipe(
      take(1),
      // dispatch exactly once when nothing has been loaded yet (e.g. hard reload directly on the route)
      tap(employeeState => {
        if (!employeeState.currentEmployee?.company && !employeeState.error) {
          this.store.dispatch(EmployeeActions.getCurrentEmployee());
        }
      }),
      switchMap(() => employeeState$),
      // note: the initial value of currentEmployee is an empty array, therefore check for the company
      filter(employeeState => !!employeeState.currentEmployee?.company || !!employeeState.error),
      take(1),
      map(employeeState => companyHasFeature(employeeState.currentEmployee?.company, feature)
        ? true
        : this.router.createUrlTree(["/403"]))
    );
  }

}